/**
 * Seção de investimento.
 *
 * É a seção que o cliente lê com mais atenção, e a que o legado apresentava pior. Padrão adotado da
 * proposta da Dommus: tabela com colunas USD e BRL lado a lado, memória de cálculo por linha, e
 * total destacado no `tfoot`.
 *
 * Todos os números vêm de `resultados`, que o servidor recalcula a partir das premissas (decisão
 * D6). O renderizador não faz aritmética de preço — só formata.
 */

import {
  descricaoExibivel,
  faixaSafeStorage,
  formatarBrl,
  formatarPercentual,
  formatarUsd,
  totalMensalRecorrente,
  totalProdutosEmaster,
  type DocumentoProposta,
} from '@emaster/dominio';

import { escaparHtml } from './escapar.ts';
import { cabecalhoSecao } from './componentes.ts';
import { CONTAINER, icone } from './tokens.ts';
import { avisoInvestimento } from './vencimento.ts';

interface LinhaTabela {
  readonly item: string;
  /** Coluna `Description` do CSV: o contexto de negócio do item. Vazia quando não informada. */
  readonly descricao: string;
  readonly memoria: string;
  readonly usd: number;
  readonly brl: number | null;
}

function celulaValor(usd: number, brl: number | null, destaque: boolean): string {
  const classe = destaque
    ? 'p-4 border-l border-white/10 text-right text-lg text-brand-accent whitespace-nowrap'
    : 'p-4 border-l border-gray-100 text-right font-bold text-brand-blue whitespace-nowrap';

  const celulaBrl =
    brl === null
      ? `<td class="${classe}">&mdash;</td>`
      : `<td class="${classe}">${formatarBrl(brl)}</td>`;

  return `<td class="${classe}">${formatarUsd(usd)}</td>${celulaBrl}`;
}

/**
 * Tabela de investimento.
 *
 * A coluna `Descrição` só é emitida quando os recursos importados do CSV trazem descrição — é o
 * texto que o arquiteto escreveu na estimativa da calculadora. Numa proposta sem CSV, ou com a
 * coluna `Description` em branco, ela seria uma faixa vazia ocupando largura útil da tabela.
 *
 * A decisão não considera as linhas fixas (suporte, backup): elas têm descrição própria e sozinhas
 * manteriam a coluna aberta com um travessão em toda linha de recurso.
 */
function tabelaInvestimento(
  linhas: readonly LinhaTabela[],
  total: LinhaTabela,
  comDescricao: boolean,
): string {
  const celulaDescricao = (l: LinhaTabela): string =>
    comDescricao
      ? `<td class="p-4 border-l border-gray-100 text-gray-600">${l.descricao === '' ? '&mdash;' : escaparHtml(l.descricao)}</td>`
      : '';

  const colunaDescricao = comDescricao
    ? '<th class="p-4 font-semibold border-l border-white/10">Descrição</th>'
    : '';

  // O rótulo do total ocupa as colunas descritivas: Item + Memória, mais Descrição quando existe.
  const colunasDescritivas = comDescricao ? 3 : 2;

  const corpo = linhas
    .map(
      (l) => `<tr class="border-t border-gray-100">
                                <td class="p-4 font-semibold text-brand-blue">${escaparHtml(l.item)}</td>
                                ${celulaDescricao(l)}
                                <td class="p-4 border-l border-gray-100 text-gray-500">${escaparHtml(l.memoria)}</td>
                                ${celulaValor(l.usd, l.brl, false)}
                            </tr>`,
    )
    .join('\n                            ');

  return `<div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-4">
                <div class="overflow-x-auto">
                    <table class="w-full text-left border-collapse">
                        <thead>
                            <tr class="bg-brand-light text-white text-xs sm:text-sm uppercase tracking-wide">
                                <th class="p-4 font-semibold">Item</th>
                                ${colunaDescricao}
                                <th class="p-4 font-semibold border-l border-white/10">Memória de cálculo</th>
                                <th class="p-4 font-semibold border-l border-white/10 text-right whitespace-nowrap">Valor (USD)</th>
                                <th class="p-4 font-semibold border-l border-white/10 text-right whitespace-nowrap">Valor (BRL)</th>
                            </tr>
                        </thead>
                        <tbody class="text-gray-700 text-sm">
                            ${corpo}
                        </tbody>
                        <tfoot class="text-sm">
                            <tr class="border-t-2 border-brand-accent bg-brand-blue text-white font-extrabold">
                                <td class="p-4 text-base sm:text-lg" colspan="${String(colunasDescritivas)}">${escaparHtml(total.item)}</td>
                                ${celulaValor(total.usd, total.brl, true)}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>`;
}

/** Pílulas de moeda e câmbio declarados. Exigência da seção 6.1 do guia de propostas. */
function pilulas(doc: DocumentoProposta): string {
  const itens: string[] = [];
  const moeda = doc.premissas.trilha === 'PS' ? 'BRL (real)' : 'USD (dólar americano)';
  itens.push(
    `${icone('receipt', 'w-4 h-4 text-brand-accent')} Faturamento: <span class="text-brand-accent">${escaparHtml(moeda)}</span>`,
  );

  if (doc.premissas.ptax !== null) {
    const taxa = doc.premissas.ptax.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
    itens.push(
      `${icone('arrow-left-right', 'w-4 h-4 text-brand-accent')} Câmbio: <span class="text-brand-accent">R$ ${taxa} / US$ 1,00</span>`,
    );
  }

  const regiao = doc.premissas.regiaoAws;
  if (regiao !== null) {
    itens.push(
      `${icone('map-pin', 'w-4 h-4 text-brand-accent')} Região AWS: <span class="text-brand-accent">${escaparHtml(regiao)}</span>`,
    );
  }

  return `<div class="flex flex-wrap items-center gap-3 mb-8">
                ${itens
                  .map(
                    (i) =>
                      `<span class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-blue/5 border border-brand-blue/10 text-brand-blue text-sm font-semibold">${i}</span>`,
                  )
                  .join('\n                ')}
            </div>`;
}

/** Bloco de crédito AWS — posicionado como incentivo exclusivo com senso de urgência. */
function blocoCredito(doc: DocumentoProposta): string {
  const credito = doc.premissas.credito;
  if (credito === null || credito.valorUsd <= 0) return '';

  const r = doc.resultados;

  return `
            <div class="bg-brand-blue rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden mt-12">
                <div class="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-accent blur-3xl opacity-20 pointer-events-none"></div>
                <div class="relative z-10">
                    <div class="flex items-center gap-3 mb-4">
                        <div class="w-12 h-12 shrink-0 bg-brand-accent/20 border border-brand-accent/40 rounded-xl flex items-center justify-center text-brand-accent">${icone('hand-coins', 'w-6 h-6')}</div>
                        <div>
                            <p class="text-xs uppercase tracking-wider text-brand-accent font-semibold">Incentivo exclusivo</p>
                            <h3 class="text-2xl font-bold leading-tight">Até ${formatarUsd(credito.valorUsd)} em créditos AWS</h3>
                        </div>
                    </div>
                    <p class="text-gray-300 text-base leading-relaxed mb-6 max-w-3xl">
                        Como parceira <strong class="text-white">Advanced Tier</strong> da AWS, a eMaster pleiteia créditos exclusivos que não estão disponíveis para quem contrata a nuvem diretamente.${r.reducaoMensal.usd > 0 ? ` Isso representa uma <strong class="text-white">redução estimada de ${formatarUsd(r.reducaoMensal.usd)}/mês</strong> nos primeiros ${String(credito.janelaMeses)} meses de operação${r.exibirEconomia ? ` — <strong class="text-white">${formatarPercentual(r.economiaPercentual)} de economia</strong> no período` : ''}.` : ` O valor de <strong class="text-white">${formatarUsd(credito.valorUsd)}</strong> será aplicado para cobrir o custo de implantação, eliminando o investimento inicial.`}
                    </p>
                    <div class="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
                        <div class="flex items-start gap-4">
                            <div class="w-11 h-11 shrink-0 bg-brand-accent/20 border border-brand-accent/40 rounded-xl flex items-center justify-center text-brand-accent">${icone('clock', 'w-5 h-5')}</div>
                            <div>
                                <h4 class="font-bold text-white text-base mb-2">Este benefício está vinculado à validade desta proposta</h4>
                                <p class="text-gray-300 text-sm leading-relaxed">
                                    A aprovação dos créditos depende de análise dentro do prazo de validade. Após o vencimento, uma nova análise será necessária e o valor pode não ser mantido.
                                </p>
                            </div>
                        </div>
                    </div>
                    <p class="text-gray-500 text-xs leading-relaxed">
                        * Créditos sujeitos à aprovação da AWS. O valor representa o teto do pleito que a eMaster se compromete a solicitar.
                    </p>
                </div>
            </div>`;
}

/** Uma linha do resumo financeiro: rótulo, nota explicativa e valor nas duas moedas. */
function linhaResumo(o: {
  readonly titulo: string;
  /** HTML já montado: pode trazer destaque de economia ou valores das parcelas. */
  readonly notaHtml: string;
  readonly usd: number;
  readonly brl: number | null;
  /** `-` na linha do crédito, para o abatimento se ler como subtração. */
  readonly sinal?: string;
  readonly classeValor: string;
  readonly semBorda?: boolean;
}): string {
  const sinal = o.sinal ?? '';
  const borda = o.semBorda === true ? '' : ' border-b border-gray-100';

  const emBrl =
    o.brl === null
      ? ''
      : `<p class="whitespace-nowrap text-sm text-gray-500">${sinal}${formatarBrl(o.brl)}</p>`;

  return `
                <div class="flex flex-col gap-2 py-6 sm:flex-row sm:items-center sm:justify-between${borda}">
                    <div class="max-w-xl">
                        <p class="text-base font-bold text-brand-blue">${escaparHtml(o.titulo)}</p>
                        <p class="mt-1 text-sm leading-relaxed text-gray-500">${o.notaHtml}</p>
                    </div>
                    <div class="text-left sm:text-right">
                        <p class="whitespace-nowrap font-extrabold ${o.classeValor}">${sinal}${formatarUsd(o.usd)}</p>
                        ${emBrl}
                    </div>
                </div>`;
}

/**
 * Enumera o que compõe o consumo total, com o valor de cada parte.
 *
 * Existe porque a soma precisa ser conferível por quem lê. "Infraestrutura, SaaS e suporte" sem os
 * números obriga o cliente a voltar à tabela e somar à mão; com os números, a linha de total se
 * explica sozinha e o vendedor tem o que apontar.
 *
 * Parte zerada é omitida: proposta sem plano de suporte mostraria "Plano de suporte US$ 0,00", o que
 * levanta a pergunta errada.
 */
function composicaoDoConsumo(doc: DocumentoProposta): string {
  const r = doc.resultados;
  const saas = totalProdutosEmaster(r);

  const partes: string[] = [];
  if (r.consumo.usd > 0) partes.push(`Infraestrutura AWS ${formatarUsd(r.consumo.usd)}`);
  if (saas.usd > 0) partes.push(`Produtos eMaster ${formatarUsd(saas.usd)}`);
  if (r.custoSuporte.usd > 0) partes.push(`Plano de suporte ${formatarUsd(r.custoSuporte.usd)}`);

  return partes.map((p) => escaparHtml(p)).join(' &nbsp;+&nbsp; ');
}

/**
 * Resumo Financeiro — a conta que leva do preço do serviço ao valor faturado.
 *
 * O resumo existe para uma finalidade só: **deixar a aritmética visível**. São três linhas que se
 * leem de cima para baixo como uma subtração — consumo total previsto, menos o crédito AWS, igual ao
 * investimento mensal. É o que o vendedor precisa apontar na tela para explicar de onde saiu o
 * número, sem recorrer a planilha paralela.
 *
 * Duas escolhas sustentam isso:
 *
 * A primeira linha é **o mesmo valor** que fecha a tabela acima, e a nota diz isso com o nome que a
 * tabela usa. Rótulos diferentes para o mesmo número derrubam a confiança de quem confere; declarar
 * que são o mesmo número resolve, e amarra as duas peças.
 *
 * A linha do crédito traz a **redução mensal**, não o pleito total. Antes trazia o total — US$ 8.000
 * onde a conta do mês usa US$ 2.000 — e as três linhas simplesmente não fechavam na tela. O valor do
 * pleito e a janela ficam na nota, que é onde explicam sem competir com a subtração.
 *
 * O custo de deploy vai por último, depois de um separador e dizendo que é pagamento único. Ele não
 * entra na conta mensal, e no meio dela seria lido como se entrasse.
 */
function resumoFinanceiro(doc: DocumentoProposta): string {
  const r = doc.resultados;
  const credito = doc.premissas.credito;
  const temCredito = credito !== null && credito.valorUsd > 0;
  const deployFinanciado =
    doc.premissas.deploy.modalidade === 'financiado' ||
    doc.premissas.deploy.modalidade === 'cortesia';

  // Sem crédito e sem deploy não há conta a explicar: o total da tabela já é o valor faturado.
  if (!temCredito && !deployFinanciado && r.deployCustoTotal.usd === 0) return '';

  const composicao = composicaoDoConsumo(doc);
  const itens: string[] = [];

  if (temCredito) {
    /*
     * O total sem incentivo é recomposto aqui, e não lido de um campo de `resultados`: é a mesma soma
     * que a linha de total da tabela faz, com o mesmo auxiliar do domínio. Somar as parcelas já
     * arredondadas, em vez de converter a soma, é o que garante que as partes fechem no total exibido
     * — cada parcela já é exata ao centavo.
     */
    const consumoTotal = totalMensalRecorrente(r);

    itens.push(
      linhaResumo({
        titulo: 'Consumo total previsto',
        notaHtml: `${composicao}.<br>É o <strong>Total Mensal Estimado</strong> da tabela acima, antes de qualquer incentivo.`,
        usd: consumoTotal.usd,
        brl: consumoTotal.brl,
        classeValor: 'text-2xl text-brand-blue',
      }),
    );

    itens.push(
      linhaResumo({
        titulo: 'Crédito Exclusivo AWS via eMaster',
        /*
         * Sem tratar singular: a janela é `3 | 4 | 5 | 6 | 12` no domínio, e o TypeScript recusou a
         * comparação com 1 por não haver sobreposição. Guarda de plural aqui seria código morto.
         */
        notaHtml: escaparHtml(
          `Abatimento mensal na parcela durante os primeiros ${String(credito.janelaMeses)} meses, de um pleito total de ${formatarUsd(credito.valorUsd)} que a eMaster se compromete a solicitar à AWS.`,
        ),
        usd: r.reducaoMensal.usd,
        brl: r.reducaoMensal.brl,
        sinal: '-',
        classeValor: 'text-2xl text-green-600',
      }),
    );
  }

  const economiaTexto = r.exibirEconomia
    ? `<span class="font-semibold text-green-600">Economia estimada de ${formatarPercentual(r.economiaPercentual)}.</span> `
    : '';

  // Sem crédito, a composição não apareceu acima e é aqui que ela precisa constar.
  const notaInvestimento = temCredito
    ? `${economiaTexto}Valor faturado por mês durante a janela do crédito.`
    : `${composicao}.<br>Valor mensal recorrente.`;

  itens.push(
    linhaResumo({
      titulo: 'Investimento Mensal Estimado',
      notaHtml: notaInvestimento,
      usd: r.mensalidadeEstimada.usd,
      brl: r.mensalidadeEstimada.brl,
      classeValor: 'text-3xl text-brand-accent',
      semBorda: true,
    }),
  );

  let deploy = '';
  if (r.deployCustoTotal.usd > 0) {
    const notaDeploy =
      doc.premissas.deploy.modalidade === 'cortesia'
        ? 'Benefício concedido nesta proposta. Pagamento único, fora da conta mensal acima.'
        : deployFinanciado
          ? 'Coberto pelo crédito especial eMaster. Pagamento único, fora da conta mensal acima.'
          : 'Pagamento único, não recorrente. Não entra na conta mensal acima.';

    deploy = `
                <div class="mt-2 border-t-2 border-dashed border-gray-200">
                    ${linhaResumo({
                      titulo: 'Custo de Deploy',
                      notaHtml: escaparHtml(notaDeploy),
                      usd: r.deployCustoTotal.usd,
                      brl: r.deployCustoTotal.brl,
                      classeValor: `text-2xl ${deployFinanciado ? 'text-green-600' : 'text-brand-blue'}`,
                      semBorda: true,
                    })}
                </div>`;
  }

  return `
            <div class="mt-10 rounded-3xl border border-gray-100 bg-white p-8 shadow-sm">
                <div class="mb-8 text-center">
                    <h3 class="text-2xl font-extrabold text-brand-blue">Resumo Financeiro</h3>
                    <p class="mx-auto mt-2 max-w-xl text-sm text-gray-500">
                        Como se chega ao valor faturado: o total do que foi dimensionado, menos o incentivo que a eMaster pleiteia junto à AWS como parceira Advanced.
                    </p>
                </div>
                ${itens.join('')}
                ${deploy}
            </div>`;
}

export function secaoInvestimento(doc: DocumentoProposta, hoje?: string): string {
  const r = doc.resultados;
  const linhas: LinhaTabela[] = [];

  // Quando há itens do CSV, detalhamos serviço a serviço como o legado faz. Quando não há, é uma
  // linha consolidada com o consumo manual.
  const itensDocsv = doc.premissas.itensCusto;
  const margem = r.margemPercentual;
  const ptax = doc.premissas.ptax;

  // A coluna Descrição existe apenas se algum recurso do CSV tiver a coluna `Description` preenchida.
  const comDescricao = itensDocsv?.some((i) => descricaoExibivel(i.descricao) !== '') === true;

  if (r.margemAplicada && itensDocsv !== null && itensDocsv.length > 0) {
    for (const item of itensDocsv) {
      const precoVenda = item.custoMensalLiquidoUsd / (1 - margem / 100);
      const precoArredondado = Math.round(precoVenda * 100) / 100;
      linhas.push({
        item: item.servico,
        descricao: descricaoExibivel(item.descricao),
        memoria: item.resumoConfiguracao,
        usd: precoArredondado,
        brl: ptax !== null ? Math.round(precoArredondado * ptax * 100) / 100 : null,
      });
    }
  } else {
    linhas.push({
      item: 'Infraestrutura AWS',
      descricao: '',
      memoria: r.margemAplicada
        ? `Dimensionada com margem de gestão de ${r.margemPercentual}%`
        : 'Conforme dimensionamento acordado',
      usd: r.consumo.usd,
      brl: r.consumo.brl,
    });
  }

  if (r.custoSuporte.usd > 0) {
    linhas.push({
      item: `Plano de suporte SafePlace`,
      descricao: 'Operação assistida do ambiente AWS',
      memoria: 'Sustentação, monitoramento e acompanhamento consultivo',
      usd: r.custoSuporte.usd,
      brl: r.custoSuporte.brl,
    });
  }

  if (r.licencasBackup.usd > 0) {
    /*
     * O backup é o único item da proposta com preço de tabela em reais, ao lado de recursos AWS
     * cotados em dólar. Em CS, onde o faturamento é em dólar, dizer só "licenças mensais" deixaria o
     * cliente supondo que o valor em dólar é fixo — quando o que está fixo é o valor em reais, e a
     * coluna USD é conversão pelo câmbio declarado nas pílulas acima. Declarar a base evita a
     * cobrança que parece divergir da proposta no mês em que o câmbio muda.
     */
    linhas.push({
      item: 'eMaster Backup On-Premises',
      descricao: 'Proteção de dados do ambiente local',
      memoria:
        doc.premissas.trilha === 'PS'
          ? 'Licenças mensais conforme quantidade contratada'
          : 'Licenças mensais com preço fixo em reais, convertido pelo câmbio declarado',
      usd: r.licencasBackup.usd,
      brl: r.licencasBackup.brl,
    });
  }

  if (r.plataformaSafeStorage.usd > 0 && doc.premissas.safestorage !== null) {
    /*
     * A memória de cálculo declara a faixa e a contagem de usuários, e diz que o armazenamento está
     * fora desta linha. É a dúvida imediata de quem lê a proposta: se a assinatura é por usuário,
     * onde está o disco. Sem essa frase, o cliente soma a assinatura e conclui que o total já cobre o
     * armazenamento — e a primeira fatura contraria a proposta.
     */
    const s = doc.premissas.safestorage;
    const faixa = faixaSafeStorage(s.plano);

    linhas.push({
      item: faixa.nomeComercial,
      descricao: 'Plataforma de arquivos corporativos na AWS',
      memoria: `Assinatura mensal para ${String(s.usuarios)} ${
        s.usuarios === 1 ? 'usuário' : 'usuários'
      } · armazenamento S3 cobrado nas linhas de infraestrutura`,
      usd: r.plataformaSafeStorage.usd,
      brl: r.plataformaSafeStorage.brl,
    });
  }

  if (r.custosAdicionais.usd > 0) {
    /*
     * Uma linha só, alimentada pelo total que o motor já arredondou.
     *
     * A alternativa — uma linha por item — exigiria converter cada valor aqui, e a soma das
     * conversões divergiria da conversão da soma por centavos. O resultado seria a tabela não fechar,
     * que é o defeito mais corrosivo que este documento admite: o cliente soma as linhas e chega a um
     * número diferente do total impresso.
     *
     * Com um único item, que é o caso comum, o nome do produto aparece como o vendedor o escreveu.
     * Com vários, a memória de cálculo lista todos, então nada se perde além de uma linha por item.
     */
    const itens = doc.premissas.custosAdicionais ?? [];
    const unico = itens.length === 1 ? itens[0] : undefined;

    linhas.push({
      item: unico !== undefined ? unico.descricao : 'Custos adicionais',
      descricao: 'Serviço contratado à parte',
      memoria:
        unico !== undefined
          ? 'Valor mensal acordado, sem variação por consumo'
          : `Inclui ${itens.map((i) => i.descricao).join(' · ')}`,
      usd: r.custosAdicionais.usd,
      brl: r.custosAdicionais.brl,
    });
  }

  /*
   * A tabela fecha no que ela própria lista: infraestrutura com margem, suporte, backup e a
   * assinatura do SafeStorage. O crédito AWS não entra aqui.
   *
   * Motivo: o crédito é um incentivo condicionado à aprovação da AWS, e abatê-lo na linha de total
   * misturaria preço com desconto incerto — o cliente perderia a referência do que custa o serviço
   * e passaria a tratar o valor com crédito como o preço contratado. O abatimento aparece uma única
   * vez, no Resumo Financeiro, como "Investimento Mensal Estimado". É a apresentação do construtor
   * anterior, onde a tabela fechava em "Total Mensal Estimado" e o resumo trazia o efeito dos
   * incentivos.
   */
  const totalTabela = totalMensalRecorrente(r);

  const total: LinhaTabela = {
    item: 'Total Mensal Estimado',
    descricao: '',
    memoria: '',
    usd: totalTabela.usd,
    brl: totalTabela.brl,
  };

  const semPlano = r.custoSuporte.usd === 0;
  const notaSemPlano = semPlano
    ? `<div class="bg-blue-50 border-l-4 border-brand-accent p-6 rounded-r-xl mb-8">
                <h4 class="font-bold text-brand-blue mb-2">Modelo sem plano de suporte gerenciado</h4>
                <p class="text-sm text-gray-600 leading-relaxed">
                    Nesta proposta a operação do ambiente permanece com a equipe da ${escaparHtml(doc.cliente)}. A eMaster atua como parceira de faturamento em reais e como porta de acesso ao ecossistema AWS, incluindo os times especializados e os programas de crédito da fabricante.
                </p>
            </div>`
    : '';

  const deploy = doc.premissas.deploy.modalidade === 'nao_incluido' ? '' : blocoDeploy(doc);

  return `
    <section id="investimento" class="scroll-mt-24 py-20 bg-gray-50 border-t border-gray-200">
        <div class="${CONTAINER}">
            ${cabecalhoSecao('Investimento', 'A composição do seu investimento mensal')}
            ${avisoInvestimento(doc, hoje)}
            ${pilulas(doc)}
            ${notaSemPlano}
            ${tabelaInvestimento(linhas, total, comDescricao)}
            ${graficoDistribuicao(doc)}
            <p class="text-xs text-gray-500 leading-relaxed mb-4">
                Valores mensais recorrentes. O consumo de infraestrutura é medido na própria conta AWS e faturado conforme o uso efetivo do mês.
            </p>
            ${deploy}
            ${blocoCredito(doc)}
            ${resumoFinanceiro(doc)}
        </div>
    </section>`;
}

/** Gráfico de pizza com a distribuição dos custos mensais. Usa Chart.js via CDN. */
function graficoDistribuicao(doc: DocumentoProposta): string {
  const r = doc.resultados;
  const fatias: { rotulo: string; valor: number }[] = [];

  // Detalha por serviço quando há CSV, como a tabela faz.
  const itensDocsv = doc.premissas.itensCusto;
  const margem = r.margemPercentual;

  if (r.margemAplicada && itensDocsv !== null && itensDocsv.length > 0) {
    for (const item of itensDocsv) {
      const precoVenda = Math.round((item.custoMensalLiquidoUsd / (1 - margem / 100)) * 100) / 100;
      // Item de custo zero fica fora do gráfico, mas continua na tabela. Fatia de 0% não desenha
      // nada e só ocupa uma linha da legenda, empurrando para baixo o que o cliente precisa ler.
      if (precoVenda > 0) fatias.push({ rotulo: item.servico, valor: precoVenda });
    }
  } else if (r.consumo.usd > 0) {
    fatias.push({ rotulo: 'Infraestrutura AWS', valor: r.consumo.usd });
  }

  if (r.custoSuporte.usd > 0)
    fatias.push({ rotulo: 'Plano de Suporte SafePlace', valor: r.custoSuporte.usd });
  if (r.licencasBackup.usd > 0)
    fatias.push({ rotulo: 'eMaster Backup', valor: r.licencasBackup.usd });
  if (r.plataformaSafeStorage.usd > 0)
    fatias.push({ rotulo: 'eMaster SafeStorage', valor: r.plataformaSafeStorage.usd });
  if (r.custosAdicionais.usd > 0)
    fatias.push({ rotulo: 'Custos adicionais', valor: r.custosAdicionais.usd });

  if (fatias.length === 0) return '';

  const total = fatias.reduce((acc, f) => acc + f.valor, 0);
  const cores = [
    '#2563EB',
    '#16A34A',
    '#9E0B3B',
    '#5D7A90',
    '#F59E0B',
    '#8FB8CB',
    '#EF4444',
    '#232F3E',
    '#D7DCDF',
  ];
  const rotulos = JSON.stringify(fatias.map((f) => f.rotulo));
  const dados = JSON.stringify(fatias.map((f) => f.valor.toFixed(2)));
  const coresJson = JSON.stringify(cores.slice(0, fatias.length));

  // Legenda customizada em HTML ao lado, com percentuais.
  const legendaHtml = fatias
    .map((f, i) => {
      const pct = total > 0 ? ((f.valor / total) * 100).toFixed(1) : '0.0';
      const cor = cores[i % cores.length] ?? '#999';
      return `<li class="flex items-center gap-2">
                            <span class="shrink-0 w-3 h-3 rounded-sm" style="background:${cor}"></span>
                            <span class="text-gray-700 text-xs leading-tight flex-1">${escaparHtml(f.rotulo)}</span>
                            <span class="text-brand-blue font-bold text-xs whitespace-nowrap">${pct}%</span>
                        </li>`;
    })
    .join('\n                        ');

  const script = `
    (function() {
      var ctx = document.getElementById('chartDistribuicao');
      if (!ctx || typeof Chart === 'undefined') return;
      new Chart(ctx, {
        type: 'pie',
        data: {
          labels: ${rotulos},
          datasets: [{
            data: ${dados},
            backgroundColor: ${coresJson},
            borderColor: '#ffffff',
            borderWidth: 4,
            hoverOffset: 8
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: function(c) { return ' ' + c.label + ': US$ ' + parseFloat(c.parsed).toLocaleString('pt-BR', {minimumFractionDigits:2}) + ' (' + ((parseFloat(c.parsed)/${total.toFixed(2)})*100).toFixed(1) + '%)'; } } }
          }
        }
      });
    })();`;

  return `
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                <h3 class="font-bold text-brand-blue mb-6 text-center">Distribuição do investimento mensal</h3>
                <div class="grid grid-cols-1 sm:grid-cols-5 gap-6 items-center">
                    <div class="sm:col-span-3" style="height:280px;">
                        <canvas id="chartDistribuicao"></canvas>
                    </div>
                    <div class="sm:col-span-2">
                        <ul class="space-y-3">
                        ${legendaHtml}
                        </ul>
                    </div>
                </div>
                <script>${script}</script>
            </div>`;
}

/** Custo de implantação. Pagamento único, nunca recorrente. */
function blocoDeploy(doc: DocumentoProposta): string {
  const r = doc.resultados;
  const modalidade = doc.premissas.deploy.modalidade;

  const rotulo = modalidade === 'cortesia' ? 'Implantação (cortesia)' : 'Implantação';
  const nota =
    modalidade === 'cortesia'
      ? 'Benefício concedido nesta proposta: nada a faturar.'
      : modalidade === 'financiado'
        ? 'Coberto pelo crédito especial AWS via eMaster.'
        : 'Pagamento único, não recorrente.';

  const corValor = modalidade === 'faturado' ? 'text-brand-blue' : 'text-green-600';

  return `<div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm mb-4">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h4 class="font-bold text-brand-blue text-lg">${escaparHtml(rotulo)}</h4>
                        <p class="text-sm text-gray-500 mt-1">${escaparHtml(nota)}</p>
                    </div>
                    <div class="text-left sm:text-right">
                        <p class="text-3xl font-extrabold ${corValor}">${formatarUsd(r.deployCustoTotal.usd)}</p>
                        ${r.deployCustoTotal.brl === null ? '' : `<p class="text-sm text-gray-500">${formatarBrl(r.deployCustoTotal.brl)}</p>`}
                        ${r.deployFaturado.usd === 0 ? '<p class="text-xs font-semibold text-green-600 mt-1">Sem custo para o cliente</p>' : ''}
                    </div>
                </div>
            </div>`;
}
